/**
 * ============================================
 * DATE RANGE CONTEXT
 * ============================================
 *
 * A single, dashboard-wide date-range filter shared across the Analytics,
 * Friend Code and Sony/DOMO tabs. Because those tabs mount/unmount as the
 * user switches modes (see Dashboard.tsx), holding the selection in a context
 * keeps it stable across tab switches — one filter, applied everywhere.
 *
 * This is a PURELY CLIENT-SIDE slice: the backend snapshot already ships the
 * time-series charts keyed by ISO date (YYYY-MM-DD), so a range just filters
 * which of those keys each chart renders. No backend / worker changes, no
 * resync. Aggregate all-time cards (Total Players, geo, level stats, …) are
 * NOT affected because they are stored as all-time totals only.
 */

import { createContext, useContext, useState, useMemo, useCallback } from 'react';

// ============================================
// TYPES
// ============================================

export type DateRangePreset = '7d' | '30d' | '90d' | 'all' | 'custom';

export interface DateRangeValue {
  preset: DateRangePreset;
  /** Inclusive lower bound (ISO YYYY-MM-DD), or null when unbounded. */
  start: string | null;
  /** Inclusive upper bound (ISO YYYY-MM-DD), or null when unbounded. */
  end: string | null;
  /** true when the range is the default "all time" (no slicing applied). */
  isAllTime: boolean;
  setPreset: (preset: DateRangePreset) => void;
  /** Set an explicit custom range; switches preset to 'custom'. */
  setCustom: (start: string | null, end: string | null) => void;
  /**
   * Predicate for a single ISO date. Accepts values with an optional time
   * component (e.g. "2026-01-02T00:00:00") — only the first 10 chars matter.
   */
  inRange: (dateStr: string | undefined | null) => boolean;
}

// ============================================
// HELPERS
// ============================================

/** Local-timezone ISO date (YYYY-MM-DD), matching how the charts key days. */
function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** ISO date `n` days before today (local). */
function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIso(d);
}

// ============================================
// CONTEXT
// ============================================

const DateRangeContext = createContext<DateRangeValue | null>(null);

export function DateRangeProvider({ children }: { children: React.ReactNode }) {
  const [preset, setPresetState] = useState<DateRangePreset>('all');
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);

  // Resolve the concrete [start, end] window from the active preset.
  const { start, end } = useMemo(() => {
    const today = toIso(new Date());
    switch (preset) {
      case '7d':
        return { start: isoDaysAgo(6), end: today }; // last 7 days incl. today
      case '30d':
        return { start: isoDaysAgo(29), end: today };
      case '90d':
        return { start: isoDaysAgo(89), end: today };
      case 'custom':
        return { start: customStart, end: customEnd };
      case 'all':
      default:
        return { start: null, end: null };
    }
  }, [preset, customStart, customEnd]);

  const setPreset = useCallback((p: DateRangePreset) => {
    setPresetState(p);
  }, []);

  const setCustom = useCallback((s: string | null, e: string | null) => {
    setCustomStart(s || null);
    setCustomEnd(e || null);
    setPresetState('custom');
  }, []);

  const inRange = useCallback(
    (dateStr: string | undefined | null): boolean => {
      if (!dateStr) return false;
      const d = dateStr.slice(0, 10);
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    },
    [start, end]
  );

  const value = useMemo<DateRangeValue>(
    () => ({
      preset,
      start,
      end,
      isAllTime: preset === 'all' || (start === null && end === null),
      setPreset,
      setCustom,
      inRange,
    }),
    [preset, start, end, setPreset, setCustom, inRange]
  );

  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

export function useDateRange(): DateRangeValue {
  const ctx = useContext(DateRangeContext);
  if (!ctx) {
    throw new Error('useDateRange must be used within a DateRangeProvider');
  }
  return ctx;
}
